import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { MANUFACTURER_PATHS } from 'routes/routesConstants';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate } from 'utils/dateFormatter';

interface Manufacturer {
  id: string;
  name: string;
  description?: string | null;
  country?: string | null;
  isActive: boolean;
  updatedAt: string;
}

const ManufacturerList: React.FC = () => {
  const navigate = useNavigate();

  const columns = useMemo<DataTableColumn<Manufacturer>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<Manufacturer, unknown>) => (
          <Link to={MANUFACTURER_PATHS.EDIT_MANUFACTURER.replace(':id', info.row.original.id)} className="text-dark fw-bold">
            {info.getValue() as string}
          </Link>
        ),
      },
      {
        accessorKey: 'country',
        id: 'country',
        header: 'Country',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<Manufacturer, unknown>) => info.getValue() || '-',
      },
      {
        accessorKey: 'description',
        id: 'description',
        header: 'Description',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<Manufacturer, unknown>) => {
          const value = info.getValue() as string | null | undefined;
          return value ? <small>{value.substring(0, 80)}{value.length > 80 ? '...' : ''}</small> : '-';
        },
      },
      {
        accessorKey: 'isActive',
        id: 'isActive',
        header: 'Status',
        enableSorting: true,
        enableFiltering: true,
        filterType: 'checkbox',
        cell: (info: CellContext<Manufacturer, unknown>) => (
          <span className={`badge ${info.getValue() ? 'bg-success' : 'bg-danger'}`}>
            {info.getValue() ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<Manufacturer, unknown>) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return <span className={`small ${isStale ? 'text-danger' : 'text-muted'}`}>{text}</span>;
        },
      },
      {
        accessorKey: 'actions',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<Manufacturer, unknown>) => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigate(MANUFACTURER_PATHS.EDIT_MANUFACTURER.replace(':id', row.id))}
              >
                <i className="ri-edit-line"></i> Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (window.confirm(`Delete manufacturer "${row.name}"?`)) {
                    console.log('Delete manufacturer:', row.id);
                  }
                }}
              >
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          );
        },
      },
    ],
    [navigate],
  );

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<Manufacturer>> => {
    return DataTableApiService.fetchPaginatedData<Manufacturer>(API_ENDPOINTS.TENANT.MANUFACTURERS.BASE, params);
  };

  const handleAddManufacturer = () => {
    navigate(MANUFACTURER_PATHS.ADD_MANUFACTURER);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Manufacturers" breadcrumbItem="List" />
        <Row>
          <Col lg="12">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Manufacturers</h5>
              <button className="btn btn-success" onClick={handleAddManufacturer}>
                <i className="ri-add-line"></i> Add Manufacturer
              </button>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.MANUFACTURERS}
              onFetchData={handleFetchData}
              enableSearch={true}
              enableFiltering={true}
              enablePagination={true}
              enableSorting={true}
              enableColumnVisibility={true}
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport={true}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="manufacturer_list_visibility"
              columnOrderStorageKey="manufacturer_list_order"
              title=""
              emptyMessage="No manufacturers found. Add one to get started."
              striped={true}
              hover={true}
              bordered={true}
              responsive={true}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ManufacturerList;
